export const SUPPORTED_MODEM_REBOOT_EXACT = new Set([
  'Huawei E3372h-153',
  'Huawei E8372h-153',
]);

export const MODEM_MODEL_PLACEHOLDER_VALUES = new Set(['', 'unknown']);

export const KRAKEN_MODEM_TYPE_FALLBACK = {
  1: 'Huawei E3372h-153',
  2: 'ZTE MF833R',
  3: 'ZTE MF920RU',
  4: 'Huawei E3372h-320',
  5: 'ZTE MF823D',
  6: 'ZTE MF79U',
  7: 'Huawei E3372h-510',
  8: 'ZTE MF79U-1',
  9: 'ZTE MF79U-2',
  10: 'ZTE MF79U-3',
  11: 'Huawei E3372h-607',
  12: 'ZTE MF79U-4',
  13: 'Huawei E3372h-608',
  14: 'ZTE MF79U-5',
  15: 'ZTE MF79U-6',
  16: 'ZTE MF79U-7',
  17: 'ZTE MF79U-8',
  18: 'Huawei E3372h-609',
  19: 'ZTE MF79U-9',
  20: 'ZTE MF79U-10',
  21: 'Huawei E3372h-610',
  22: 'ZTE MF79U-11',
  23: 'ZTE MF79U-12',
  24: 'Huawei E3372h-611',
  25: 'ZTE MF79U-13',
  26: 'ZTE MF79U-14',
  27: 'Huawei E3372h-612',
  28: 'ZTE MF79U-15',
  29: 'ZTE MF79U-16',
  30: 'Huawei E3372h-613',
};

const stripDisplayModemPrefix = (value) => String(value || '').replace(/^№\s*\d+\s*/i, '').trim();

const isMeaningfulModemModelValue = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return false;
  return !MODEM_MODEL_PLACEHOLDER_VALUES.has(normalized.toLowerCase());
};

const looksLikePortLabel = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return true;
  if (/^port-\d+$/i.test(normalized)) return true;
  if (/\b(?:хаб|hub)\b[\s\d.-]*\bport-\d+\b/i.test(normalized)) return true;
  if (/\bport-\d+\b/i.test(normalized) && !/^zte\b/i.test(normalized) && !/^huawei\b/i.test(normalized)) {
    return true;
  }
  return false;
};

export const resolveKrakenModemTypeName = (typeId, modemTypes = []) => {
  if (typeof typeId === 'string' && !/^\d+$/.test(typeId.trim())) {
    return typeId.trim();
  }

  const numericId = Number(typeId);
  if (!Number.isFinite(numericId)) {
    return '';
  }

  const fromApi = modemTypes.find(
    (type) => type?.id === numericId || type?.id === typeId || Number(type?.id) === numericId
  );
  if (fromApi?.name) {
    return String(fromApi.name).trim();
  }

  if (KRAKEN_MODEM_TYPE_FALLBACK[numericId]) {
    return KRAKEN_MODEM_TYPE_FALLBACK[numericId];
  }

  if (numericId > 30) {
    return `Modem Type ${numericId}`;
  }
  if (numericId >= 16) {
    return `ZTE MF79U-${numericId - 6}`;
  }

  return `Type ${numericId}`;
};

export const resolveModemRebootModelName = (record, { modemTypes = [] } = {}) => {
  if (!record || typeof record !== 'object') {
    return '';
  }

  const candidates = [];
  const seen = new Set();

  const addCandidate = (value) => {
    const normalized = stripDisplayModemPrefix(value);
    if (!normalized || !isMeaningfulModemModelValue(normalized)) return;
    const key = normalized.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    candidates.push(normalized);
  };

  const addTypeId = (typeId) => {
    addCandidate(resolveKrakenModemTypeName(typeId, modemTypes));
  };

  const addRawField = (value) => {
    const normalized = stripDisplayModemPrefix(value);
    if (!normalized || !isMeaningfulModemModelValue(normalized)) return;
    if (/^\d+$/.test(normalized)) {
      addTypeId(normalized);
      return;
    }
    if (looksLikePortLabel(normalized)) return;
    addCandidate(normalized);
  };

  addRawField(record.modem_type);
  addRawField(record.modem_model);
  if (record.type !== undefined && record.type !== null && record.type !== '') {
    addTypeId(record.type);
  }
  addRawField(record.name);
  addRawField(record.modemName);

  return candidates.find(isSupportedModemRebootModel) || candidates[0] || '';
};

export const isSupportedModemRebootModel = (modelName) => {
  const value = String(modelName || '').trim();
  if (!value) return false;
  if (SUPPORTED_MODEM_REBOOT_EXACT.has(value)) return true;
  return value.startsWith('ZTE');
};

// Человекочитаемые описания кодов-состояний перезагрузки (last_error / diagnosis / skip reason).
// Коды остаются в БД/логах как есть; здесь — только для UI, чтобы было понятно, что за состояние.
export const MODEM_REBOOT_REASON_TRANSLATIONS = {
  // Диагнозы post-check после перезагрузки
  modem_sim_undetected_after_reboot: 'SIM-карта не обнаружена после перезагрузки',
  modem_undetected_after_reboot: 'Модем не обнаружен после перезагрузки',
  ppp_disconnected_after_reboot: 'Нет интернет-соединения (PPP разорвано) после перезагрузки',
  zte_status_empty_after_reboot: 'Модем не вернул статус (не успел загрузиться) после перезагрузки',
  modem_web_ui_unavailable_after_reboot: 'Web UI модема не ответил после перезагрузки',
  zte_post_reboot_probe_failed: 'Web UI модема не ответил после перезагрузки',
  unknown_post_reboot_error: 'Неизвестная ошибка проверки после перезагрузки',
  unknown_reboot_error: 'Неизвестная ошибка перезагрузки',
  // Ошибки команды перезагрузки через web API модема
  zte_login_failed: 'Не удалось авторизоваться в web UI модема (ZTE)',
  zte_reboot_failed: 'Модем отклонил команду REBOOT_DEVICE (ZTE web API)',
  zte_modem_unreachable: 'Web UI модема ZTE недоступен по LAN IP',
  huawei_login_failed: 'Не удалось получить сессию HiLink (Huawei)',
  huawei_reboot_failed: 'Модем отклонил команду reboot (Huawei HiLink API)',
  huawei_modem_unreachable: 'Web UI модема Huawei недоступен по LAN IP',
  // Причины отказа/пропуска (eligibility / skip)
  modem_not_found: 'Модем не найден',
  reboot_rule_disabled: 'Автоперезагрузка отключена в правиле проверки',
  unsupported_modem_model: 'Модель модема не поддерживает перезагрузку',
  modem_not_unavailable: 'Модем доступен — перезагрузка не требуется',
  reboot_already_in_progress: 'Перезагрузка уже выполняется',
  reboot_in_progress_stale: 'Прошлый reboot завис — снят watchdog',
  cooldown_active: 'Действует пауза между перезагрузками (cooldown)',
  mass_outage_guard: 'Массовый сбой на сервере — перезагрузка заблокирована',
  missing_connection_id: 'У модема не указано подключение (connection)',
  missing_modem_ip: 'У модема не указан IP-адрес',
  modem_disabled_in_kraken: 'Модем отключён в Kraken',
};

// Переводит код-состояние в русское описание. Неизвестные значения (в т.ч. уже готовый
// русский текст с бэка) возвращает как есть.
export const translateModemRebootReason = (value) => {
  if (value === null || value === undefined) return '';
  const normalized = String(value).trim();
  if (!normalized) return '';
  return MODEM_REBOOT_REASON_TRANSLATIONS[normalized.toLowerCase()] || normalized;
};

export const isUnavailableModemStatus = (statusValue) => {
  const normalized = String(statusValue || '').trim().toLowerCase();
  return ['not_working', 'unavailable', 'unknown', 'недоступен'].includes(normalized);
};

export const isRebootCooldownActive = (state) => {
  const cooldownUntil = state?.cooldown_until;
  if (!cooldownUntil) return false;
  const untilMs = Date.parse(cooldownUntil);
  return Number.isFinite(untilMs) && untilMs > Date.now();
};

export const buildModemRebootStateMap = (payload) => {
  const states = payload?.data?.states || payload?.states || [];
  return states.reduce((acc, item) => {
    const modemId = Number(item?.modem_id || 0);
    if (modemId > 0) {
      acc[modemId] = item;
    }
    return acc;
  }, {});
};

const formatRebootResultLine = (item) => {
  const name = item?.modemName || `#${item?.modemId || 'unknown'}`;
  if (item?.status === 'success') {
    return `${name}: команда отправлена, модем перезагрузился (web UI доступен)`;
  }
  if (item?.status === 'in_progress') {
    return `${name}: перезагрузка выполняется...`;
  }
  const reason = translateModemRebootReason(item?.error) || 'ошибка перезагрузки';
  return `${name}: ${reason}`;
};

export const formatRebootCompletionBanner = (summary, { phase = 'completed' } = {}) => {
  if (!summary) {
    return { tone: 'error', text: 'Не удалось получить статус перезагрузки.' };
  }

  const { results = [], skipped = [], timedOut = false } = summary;
  const total = summary.total || results.length;
  const processed = results.filter((item) => item.status !== 'in_progress').length;
  const inProgressCount = results.filter((item) => item.status === 'in_progress').length;

  if (phase === 'progress') {
    const processedCount = Number.isFinite(Number(summary.processed))
      ? Number(summary.processed)
      : results.filter((item) => item.status !== 'in_progress').length;
    const totalCount = Number.isFinite(Number(summary.total)) ? Number(summary.total) : total;
    const progressText = totalCount > 1
      ? `Перезагрузка модемов: команда принята, ожидаем web UI (${processedCount}/${totalCount})...`
      : 'Перезагрузка модема: команда принята, ожидаем возвращения web UI...';
    return { tone: 'success', text: progressText };
  }

  if (summary.isSingle) {
    const single = results[0] || null;
    if (single?.status === 'success') {
      return {
        tone: 'success',
        text: `Модем "${single.modemName}" перезагружен: команда отправлена, web UI снова доступен.`,
      };
    }
    const reason = translateModemRebootReason(single?.error)
      || (timedOut ? 'перезагрузка не завершилась вовремя' : 'ошибка перезагрузки');
    return {
      tone: 'error',
      text: `Модем "${single?.modemName || 'unknown'}": ${reason}`,
    };
  }

  const lines = [
    timedOut
      ? `Массовая перезагрузка: таймаут ожидания (завершено ${processed}/${total}, в процессе ${inProgressCount}).`
      : 'Массовая перезагрузка завершена.',
    `Успешно: ${summary.successCount || 0}/${total}`,
    `С ошибкой: ${(summary.errorCount || 0) + (summary.inProgressCount || 0)}`,
  ];

  if (summary.skippedCount > 0) {
    lines.push(`Пропущено: ${summary.skippedCount}`);
  }

  const detailLines = results
    .filter((item) => item.status !== 'success')
    .map(formatRebootResultLine);
  if (detailLines.length > 0) {
    lines.push('', ...detailLines);
  }

  const skippedLines = (skipped || []).map(
    (item) => `${item?.modem_name || `#${item?.modem_id || 'unknown'}`}: ${translateModemRebootReason(item?.reason) || 'пропущен'}`,
  );
  if (skippedLines.length > 0) {
    lines.push('', 'Пропущены:', ...skippedLines);
  }

  const hasErrors = timedOut
    || (summary.errorCount || 0) > 0
    || (summary.inProgressCount || 0) > 0;
  const tone = hasErrors ? 'error' : 'success';

  return { tone, text: lines.join('\n') };
};
